import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { MessageEntity, MessageOptionType } from 'src/store/chatApi';
import {
  useLazyGetMatchmakerConversationQuery,
  useLazyFetchMatchmakerMessagesQuery,
  useSendMatchmakerMessageMutation,
} from 'src/store/matchmakerChatApi';
import {
  selectMessages,
  setMessage,
  setMessages,
  clearConversation,
} from 'src/store/messagesSlice';
import { UserEntity } from 'src/store/userApi';
import { useToast } from 'src/ui/Toast';
import { Panel } from 'src/ui/Panel';
import { Alert } from 'src/ui/Alert';
import { Badge } from 'src/ui/Badge';
import { Button } from 'src/ui/Button';
import { Dropdown } from 'src/ui/Dropdown';
import { MenuItem } from 'src/ui/Dropdown';
import { EmptyState } from 'src/ui/EmptyState';
import { TypingBubble } from 'src/ui/ChatBubble';
import { Input } from 'src/ui/Input';
import { Select } from 'src/ui/Select';
import { Icon } from 'src/ui/Icon';
import { Avatar } from 'src/ui/Avatar';
import SuggestionCard, { SuggestionCardData } from './suggestionCard';
import Autocomplete from 'react-google-autocomplete';

const PAGE = 30;

interface MatchmakerChatProps {
  impersonatedUser: UserEntity | null;
  /** key → versionId previewed (sent as x-mm-prompt-overrides). */
  promptOverrides?: Record<string, number>;
  /** Reports the exact system prompt the backend sent on the last turn. */
  onSystemPrompt?: (prompt: string | null) => void;
  /** Called when user wants to pick / change the impersonated user. */
  onPickUser?: () => void;
  /** Called when user clicks "Create test user". */
  onCreateTestUser?: () => void;
  /** Loading state for create-test-user mutation (from index). */
  createStatus?: { isLoading: boolean };
  /** Whether the current admin has admin role. */
  isAdmin?: boolean;
  /** Number of prompts currently previewed. */
  previewCount?: number;
  /** Opens the Simulate dialog for the impersonated user (absent → hidden). */
  onSimulate?: () => void;
  /** Bumped on every streamed simulation turn: reload the latest messages. */
  refreshKey?: number;
  /** The impersonated user is a simulation's TEST copy. */
  isSimulationCopy?: boolean;
  /** A simulation is writing to this conversation: hide the composer. */
  simulationRunning?: boolean;
  /** Rendered above the chat panel (the simulation status and results). */
  simulationPanel?: React.ReactNode;
}

// ─── Hong (matchmaker AI) avatar ────────────────────────────────────────────

function HongAvatar({ size = 28 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--accent)',
        color: 'var(--text-on-accent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon name="wand-sparkles" size={size * 0.5} />
    </div>
  );
}

// ─── Individual message bubble ───────────────────────────────────────────────

interface MessageBubbleProps {
  message: MessageEntity;
  isCurrentUser: boolean;
  user: UserEntity;
  isLastAiMessage: boolean;
  /** Called when user submits an interactive option response */
  onSendWithOption: (msg: Partial<MessageEntity>) => void;
}

function MessageBubble({
  message,
  isCurrentUser,
  user,
  isLastAiMessage,
  onSendWithOption,
}: MessageBubbleProps) {
  const { options } = message;
  const hasSuggestion = !!options?.suggestion;
  const hasOptions = !!options?.optionType && isLastAiMessage;

  const dir = isCurrentUser ? 'out' : 'in';
  const senderName = isCurrentUser
    ? (user.profile?.name || user.email).split(' ')[0]
    : 'Hong';

  return (
    <div
      className={'chat-row ' + dir}
      style={{ alignItems: 'flex-end', gap: 8 }}
    >
      {!isCurrentUser && <HongAvatar size={28} />}
      {isCurrentUser && (
        <Avatar name={user.profile?.name || user.email} size={28} />
      )}
      <div style={{ maxWidth: '76%' }}>
        {message.text && (
          // The wrapper above caps the width at 76%; the bubble's own 74%
          // cap (theme.css) is relative to that shrink-wrapped wrapper and
          // would force even a short message onto two lines.
          <div
            className="chat-bubble"
            style={{ whiteSpace: 'pre-wrap', maxWidth: '100%' }}
          >
            {message.text}
          </div>
        )}
        {hasSuggestion && options && (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          <SuggestionCard data={options.suggestion as SuggestionCardData} />
        )}
        {hasOptions && options && (
          <OptionsInput options={options} onSend={onSendWithOption} />
        )}
        <div className="chat-meta">{senderName}</div>
      </div>
    </div>
  );
}

// ─── Interactive options renderer ────────────────────────────────────────────

interface OptionsInputProps {
  options: NonNullable<MessageEntity['options']>;
  onSend: (msg: Partial<MessageEntity>) => void;
}

function OptionsInput({ options, onSend }: OptionsInputProps) {
  const { labels, optionType, default1, default2, disableOther, placeholder } =
    options;
  const [selected, setSelected] = useState<Set<number>>(
    typeof default1 === 'number' ? new Set([default1]) : new Set()
  );
  const [selected2, setSelected2] = useState<number | undefined>(default2);
  const [otherText, setOtherText] = useState('');
  const [place, setPlace] = useState<google.maps.places.PlaceResult | null>(
    null
  );

  const hasOther = selected.has(0);

  const onClick = (val: number) => {
    let next: Set<number>;
    if (optionType === MessageOptionType.Multiple) {
      next = new Set(selected);
      if (next.has(val)) next.delete(val);
      else next.add(val);
    } else {
      next = new Set([val]);
    }
    setSelected(next);
  };

  const buildAndSend = () => {
    const newMessage: Partial<MessageEntity> = {
      selectedOptions: Array.from(selected),
    };
    switch (optionType) {
      case MessageOptionType.Dropdown:
      case MessageOptionType.Single:
      case MessageOptionType.Multiple: {
        newMessage.text = labels
          .filter((item) => selected.has(item[1]))
          .map((item) => item[0])
          .join(', ');
        break;
      }
      case MessageOptionType.Range: {
        if (!selected2) return;
        newMessage.text = labels
          .filter((item) => selected.has(item[1]))
          .map((item) => item[0])
          .join(', ');
        newMessage.text += ` - ${
          labels.find((item) => item[1] === selected2)?.[0]
        }`;
        newMessage.selectedOptions?.push(selected2);
        break;
      }
      case MessageOptionType.Location: {
        if (!place) return;
        const ac = place.address_components ?? [];
        const get = (type: string) =>
          ac.find((c) => c.types.includes(type))?.long_name ?? '';
        const loc = place.geometry?.location;
        newMessage.text = place.formatted_address;
        newMessage.selectedOptions = [
          loc ? loc.lat() : 0,
          loc ? loc.lng() : 0,
          get('locality'),
          ac.find((c) => c.types.includes('administrative_area_level_1'))
            ?.short_name ?? '',
          ac.find((c) => c.types.includes('country'))?.short_name ?? '',
          get('postal_code'),
        ];
        break;
      }
    }
    if (hasOther) {
      newMessage.otherString = otherText;
      newMessage.text = (newMessage.text ?? '') + '\n' + otherText;
    }
    onSend(newMessage);
  };

  if (optionType === MessageOptionType.Confirm) {
    if (default1 === -1) {
      return (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
          }}
        >
          All Interview Completed
        </p>
      );
    }
    return (
      <div className="row gap-2" style={{ marginTop: 10, flexWrap: 'wrap' }}>
        {labels?.map((label) => (
          <Button
            key={label[1]}
            variant={label[1] === 1 ? 'primary' : 'secondary'}
            size="sm"
            onClick={() =>
              onSend({ text: label[0], selectedOptions: [label[1]] })
            }
          >
            {label[0]}
          </Button>
        ))}
      </div>
    );
  }

  if (optionType === MessageOptionType.Location) {
    return (
      <div className="col gap-2" style={{ marginTop: 10 }}>
        <Autocomplete
          apiKey="AIzaSyBOv3-NC2CUYuTHzog4qX5H79RnEgrx7L4"
          onPlaceSelected={(p) => setPlace(p)}
          style={{ width: '100%' }}
          className="input input-sm"
        />
        <Button
          variant="primary"
          size="sm"
          leftIcon="map-pin"
          disabled={!place}
          onClick={buildAndSend}
        >
          Set location
        </Button>
      </div>
    );
  }

  if (optionType === MessageOptionType.Dropdown) {
    const firstSelected = Array.from(selected)[0];
    return (
      <div className="row gap-2" style={{ marginTop: 10 }}>
        <Select
          size="sm"
          style={{ flex: 1 }}
          value={firstSelected ?? ''}
          onChange={(e) => setSelected(new Set([Number(e.target.value)]))}
        >
          {labels?.map((label) => (
            <option key={label[1]} value={label[1]}>
              {label[0]}
            </option>
          ))}
        </Select>
        <Button
          variant="primary"
          size="sm"
          rightIcon="send"
          disabled={selected.size === 0}
          onClick={buildAndSend}
        >
          Send
        </Button>
      </div>
    );
  }

  if (optionType === MessageOptionType.Range) {
    const firstSelected = Array.from(selected)[0];
    return (
      <div className="col gap-2" style={{ marginTop: 10 }}>
        <div className="row gap-2 items-center">
          <Select
            size="sm"
            style={{ flex: 1 }}
            value={firstSelected ?? ''}
            onChange={(e) => onClick(Number(e.target.value))}
          >
            {labels?.map((label) => (
              <option key={label[1]} value={label[1]}>
                {label[0]}
              </option>
            ))}
          </Select>
          <span
            style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}
          >
            –
          </span>
          <Select
            size="sm"
            style={{ flex: 1 }}
            value={selected2 ?? ''}
            onChange={(e) => setSelected2(Number(e.target.value))}
          >
            {labels?.map((label) => (
              <option key={label[1]} value={label[1]}>
                {label[0]}
              </option>
            ))}
          </Select>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button
            variant="primary"
            size="sm"
            rightIcon="send"
            disabled={selected.size === 0}
            onClick={buildAndSend}
          >
            Send
          </Button>
        </div>
      </div>
    );
  }

  // Single / Multiple selection chips
  return (
    <div className="col gap-2" style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        {labels?.map((label) => {
          const on = selected.has(label[1]);
          return (
            <button
              key={label[1]}
              className={'fchip' + (on ? ' active' : '')}
              onClick={() => onClick(label[1])}
            >
              <span className="fchip-check">
                <Icon name="check" size={13} strokeWidth={3} />
              </span>
              {label[0]}
            </button>
          );
        })}
      </div>
      {hasOther && !disableOther && (
        <Input
          size="sm"
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder={placeholder || 'Other'}
        />
      )}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <Button
          variant="primary"
          size="sm"
          rightIcon="send"
          disabled={selected.size === 0}
          onClick={buildAndSend}
        >
          Send
        </Button>
      </div>
    </div>
  );
}

// ─── Free-text chat input ────────────────────────────────────────────────────

interface TextInputProps {
  disabled?: boolean;
  placeholder?: string;
  onSend: (text: string) => void;
}

function FreeTextInput({ disabled, placeholder, onSend }: TextInputProps) {
  const [text, setText] = useState('');

  const submit = () => {
    if (!text.trim() || disabled) return;
    onSend(text.trim());
    setText('');
  };

  return (
    <div className="row gap-2" style={{ alignItems: 'flex-end' }}>
      <textarea
        className="textarea"
        placeholder={placeholder || 'Message as this member…'}
        value={text}
        rows={1}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        style={{ flex: 1, minHeight: 40, maxHeight: 120, resize: 'none' }}
      />
      <Button
        variant="primary"
        iconOnly
        disabled={!text.trim() || disabled}
        onClick={submit}
        title="Send"
      >
        <Icon name="send" size={16} />
      </Button>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const MatchmakerChat = ({
  impersonatedUser,
  promptOverrides,
  onSystemPrompt,
  onPickUser,
  onCreateTestUser,
  createStatus,
  isAdmin,
  previewCount = 0,
  onSimulate,
  refreshKey = 0,
  isSimulationCopy,
  simulationRunning,
  simulationPanel,
}: MatchmakerChatProps) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [currentConv, setCurrentConv] = useState<{
    id: string;
    conversationId: string;
  } | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [getConversation, convStatus] = useLazyGetMatchmakerConversationQuery();
  const [fetchMsgs] = useLazyFetchMatchmakerMessagesQuery();
  const [sendMessage, sendStatus] = useSendMatchmakerMessageMutation();

  const messages = useSelector(
    selectMessages(currentConv?.conversationId || '')
  );

  const err = (title: string, error: any) =>
    toast({
      type: 'error',
      title,
      message: error?.data?.message || error?.message || error?.error,
    });

  // On user-select: load the impersonated user's single matchmaker conversation
  // (get-or-create + seed opening message for new users), then fetch the latest
  // page of messages. Mirrors the real customer flow.
  useEffect(() => {
    setCurrentConv((prev) => {
      if (prev) dispatch(clearConversation(prev.conversationId));
      return null;
    });
    setHasMore(false);
    onSystemPrompt?.(null);

    const userId = impersonatedUser?.id;
    if (!userId) return;

    let cancelled = false;
    (async () => {
      try {
        const conv = await getConversation({
          impersonateUserId: userId,
        }).unwrap();
        if (cancelled) return;
        setCurrentConv({ id: conv.id, conversationId: conv.conversationId });
        const res = await fetchMsgs({
          impersonateUserId: userId,
          userConversationId: conv.id,
          limit: PAGE,
        }).unwrap();
        if (cancelled) return;
        // Server returns newest-first; show chronological (oldest-first).
        const chrono = [...res.messages.data].reverse();
        dispatch(
          setMessages({
            conversationId: conv.conversationId,
            messages: chrono,
          })
        );
        setHasMore(res.messages.meta.hasMore);
      } catch (e) {
        if (!cancelled) err('Failed to load conversation', e);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonatedUser?.id]);

  // A simulation turn landed: fetch the newest page and merge it in, keeping
  // any older pages the admin already loaded above it.
  useEffect(() => {
    if (!refreshKey || !impersonatedUser || !currentConv) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchMsgs({
          impersonateUserId: impersonatedUser.id,
          userConversationId: currentConv.id,
          limit: PAGE,
        }).unwrap();
        if (cancelled) return;
        const newest = [...res.messages.data].reverse();
        const fresh = new Set(newest.map((m) => m.id));
        const oldest = newest[0]?.createdAt;
        const older = (messages ?? []).filter(
          (m) => !fresh.has(m.id) && (!oldest || m.createdAt < oldest)
        );
        dispatch(
          setMessages({
            conversationId: currentConv.conversationId,
            messages: [...older, ...newest],
          })
        );
        // Older pages already loaded keep their own "load older" state.
        if (!older.length) setHasMore(res.messages.meta.hasMore);
      } catch (e) {
        if (!cancelled) err('Failed to refresh messages', e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sendStatus.isLoading]);

  const loadOlder = async () => {
    if (!impersonatedUser || !currentConv || !messages?.length) return;
    const oldest = messages[0];
    setLoadingOlder(true);
    try {
      const res = await fetchMsgs({
        impersonateUserId: impersonatedUser.id,
        userConversationId: currentConv.id,
        limit: PAGE,
        createdAt__lt: oldest.createdAt,
      }).unwrap();
      const older = [...res.messages.data].reverse();
      dispatch(
        setMessages({
          conversationId: currentConv.conversationId,
          messages: [...older, ...(messages ?? [])],
        })
      );
      setHasMore(res.messages.meta.hasMore);
    } catch (e) {
      err('Failed to load older messages', e);
    } finally {
      setLoadingOlder(false);
    }
  };

  const send = async (message: Partial<MessageEntity> & { text?: string }) => {
    if (!impersonatedUser || !currentConv) {
      toast({ type: 'warning', title: 'Select a user first' });
      return;
    }
    // Render the user's message immediately (optimistic) — the server echoes it
    // back with the same id, so setMessage upserts it in place rather than
    // duplicating. The agent reply arrives when the API returns.
    const id = uuidv4();
    const now = new Date().toISOString();
    dispatch(
      setMessage({
        id,
        conversationId: currentConv.conversationId,
        text: message.text || '',
        senderId: impersonatedUser.id,
        topic: '',
        selectedOptions: message.selectedOptions,
        otherString: message.otherString,
        createdAt: now,
        updatedAt: now,
      })
    );
    try {
      const res = await sendMessage({
        impersonateUserId: impersonatedUser.id,
        userConversationId: currentConv.id,
        message: { id, ...message },
        promptOverrides,
      }).unwrap();
      if (res.message) dispatch(setMessage(res.message));
      if (res.aiResponse) dispatch(setMessage(res.aiResponse));
      if (res.debugSystemPrompt) onSystemPrompt?.(res.debugSystemPrompt);
    } catch (e) {
      err('Failed to send', e);
    }
  };

  const onSend = (text: string) => send({ text });
  const onSendWithOption = (message: Partial<MessageEntity>) => send(message);

  const lastMessage = messages?.length ? messages[messages.length - 1] : null;

  // Determine if the last AI message has interactive options that should replace
  // the free-text input (same logic as the original).
  const showFreeInput = useMemo(() => {
    if (!lastMessage) return true;
    const { optionType } = lastMessage.options || {};
    if (!optionType) return true;
    if (
      [
        MessageOptionType.Single,
        MessageOptionType.Multiple,
        MessageOptionType.Dropdown,
        MessageOptionType.Range,
        MessageOptionType.Confirm,
        MessageOptionType.Location,
      ].includes(optionType)
    ) {
      return false;
    }
    return true;
  }, [lastMessage]);

  const isLoading = convStatus.isFetching || sendStatus.isLoading;

  // Sub-line status text
  const statusLine = convStatus.isFetching
    ? 'Loading conversation…'
    : sendStatus.isLoading
    ? 'Hong is typing…'
    : null;

  // Actions dropdown + preview badge in panel header right
  const headerRight = (
    <div className="row gap-2">
      {previewCount > 0 && (
        <Badge color="purple" size="sm" leftIcon="eye">
          {previewCount} previewed
        </Badge>
      )}
      <Dropdown
        align="right"
        width={196}
        trigger={
          <Button
            variant="secondary"
            size="sm"
            leftIcon="more-vertical"
            rightIcon="chevron-down"
            loading={createStatus?.isLoading}
          >
            Actions
          </Button>
        }
      >
        <MenuItem icon="plus" onClick={onCreateTestUser}>
          Create test user
        </MenuItem>
        <MenuItem icon="user" onClick={onPickUser}>
          {impersonatedUser ? 'Change user' : 'Pick user'}
        </MenuItem>
        {impersonatedUser && onSimulate && (
          <MenuItem icon="play" onClick={onSimulate}>
            Simulate from this user…
          </MenuItem>
        )}
      </Dropdown>
    </div>
  );

  return (
    <div className="col gap-3" style={{ height: '100%', minWidth: 0 }}>
      {/* Contextual alerts — inside the chat column per design */}
      {isAdmin === false && (
        <Alert color="red" title="You are not an admin">
          Impersonation and prompt overrides will be ignored on this account.
        </Alert>
      )}
      {simulationPanel}
      {impersonatedUser && isSimulationCopy && (
        <Alert color="blue" title="Simulation copy">
          This is a TEST copy made for a simulation. The real user is untouched.
        </Alert>
      )}
      {impersonatedUser && !isSimulationCopy && (
        <Alert color="amber" title="Live impersonation">
          Messages write to{' '}
          <strong>
            {impersonatedUser.profile?.name || impersonatedUser.email}
          </strong>
          's real profile &amp; conversation.
        </Alert>
      )}

      {/* Chat panel — grows to fill remaining column height */}
      <Panel
        title="Matchmaker chat"
        icon="messages-square"
        headerRight={headerRight}
        className="grow"
        bodyClass=""
        bodyStyle={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {!impersonatedUser ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}
          >
            <EmptyState
              icon="messages-square"
              title="Pick a user to chat as"
              desc="Choose a member to impersonate and load their matchmaker conversation with Hong."
              action={
                onPickUser ? (
                  <Button
                    variant="primary"
                    leftIcon="user"
                    onClick={onPickUser}
                  >
                    Pick a user
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : (
          <>
            {/* subline — status bar */}
            <div
              className="row"
              style={{
                padding: '8px 16px',
                borderBottom: '1px solid var(--border)',
                flexShrink: 0,
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <span
                className="row gap-1 text-muted truncate"
                style={{ fontSize: 'var(--fs-xs)', minWidth: 0 }}
              >
                <span className="live-dot" />
                Chatting as{' '}
                <span
                  style={{ color: 'var(--text-secondary)', fontWeight: 500 }}
                >
                  {impersonatedUser.profile?.name || impersonatedUser.email}
                </span>
                <span className="mono" style={{ marginLeft: 2 }}>
                  {impersonatedUser.email}
                </span>
              </span>
              {statusLine && (
                <span
                  className="row gap-2"
                  style={{
                    flexShrink: 0,
                    fontSize: 'var(--fs-xs)',
                    color: sendStatus.isLoading
                      ? 'var(--accent-text)'
                      : 'var(--text-muted)',
                  }}
                >
                  {isLoading && <span className="spinner sm" />}
                  {statusLine}
                </span>
              )}
            </div>

            {/* thread */}
            <div
              ref={scrollRef}
              className="grow scroll-y"
              style={{ padding: '16px 18px', background: 'var(--bg-app)' }}
            >
              <div style={{ maxWidth: 600, margin: '0 auto' }}>
                {convStatus.isFetching ? (
                  <div
                    className="col"
                    style={{
                      alignItems: 'center',
                      padding: 30,
                      gap: 10,
                      color: 'var(--text-muted)',
                    }}
                  >
                    <span className="spinner lg" />
                    <span style={{ fontSize: 'var(--fs-sm)' }}>
                      Loading conversation…
                    </span>
                  </div>
                ) : (
                  <>
                    {hasMore && (
                      <div
                        className="row"
                        style={{ justifyContent: 'center', marginBottom: 14 }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon="chevron-up"
                          onClick={loadOlder}
                          loading={loadingOlder}
                        >
                          Load older messages
                        </Button>
                      </div>
                    )}
                    <div className="chat-time-divider">
                      Matchmaker onboarding · today
                    </div>
                    {messages?.map((message, i) => {
                      const isCurrentUser =
                        message.senderId === impersonatedUser.id;
                      const isLastAi =
                        !isCurrentUser && i === messages.length - 1;
                      return (
                        <MessageBubble
                          key={message.id}
                          message={message}
                          isCurrentUser={isCurrentUser}
                          user={impersonatedUser}
                          isLastAiMessage={isLastAi}
                          onSendWithOption={onSendWithOption}
                        />
                      );
                    })}
                    {sendStatus.isLoading && (
                      <div
                        className="chat-row in"
                        style={{ alignItems: 'flex-end', gap: 8 }}
                      >
                        <HongAvatar size={28} />
                        <TypingBubble />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* input */}
            {showFreeInput && currentConv && !simulationRunning && (
              <div
                style={{
                  padding: 12,
                  borderTop: '1px solid var(--border)',
                  flexShrink: 0,
                  background: 'var(--bg-canvas)',
                }}
              >
                <FreeTextInput
                  disabled={sendStatus.isLoading || convStatus.isFetching}
                  placeholder={
                    lastMessage?.options?.placeholder ||
                    'Message as this member…'
                  }
                  onSend={onSend}
                />
              </div>
            )}
          </>
        )}
      </Panel>
    </div>
  );
};

export default MatchmakerChat;
